import { RATE_LIMIT_MAX_REQUESTS, RATE_LIMIT_WINDOW_MS } from "../config/rateLimits";
import { supabase } from "./supabaseClient";
import type { SpreadType } from "../config/spreadConfigs";

export type { SpreadType };

const RATE_LIMIT_KEY = "fatespin_rl";
const SESSION_KEY = "fatespin_session";

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getOrCreateSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = generateId();
      try { localStorage.setItem(SESSION_KEY, id); } catch { /* storage blocked */ }
    }
    return id;
  } catch {
    return generateId();
  }
}

async function getAccessToken(): Promise<string | null> {
  if (!supabase) return null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch {
    return null;
  }
}

function authHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (token) headers["authorization"] = `Bearer ${token}`;
  return headers;
}

export class RateLimitError extends Error {
  constructor(waitMins: number) {
    super(
      `You've reached the reading limit. Please wait ${waitMins} minute${waitMins !== 1 ? "s" : ""} before your next reading.`,
    );
    this.name = "RateLimitError";
  }
}

export function isRateLimited(): string | null {
  try {
    const bypassKey = process.env.REACT_APP_RATE_LIMIT_BYPASS_KEY;
    if (bypassKey) {
      const stored = localStorage.getItem("fatespin_rl_bypass");
      if (stored === bypassKey) return null;
    }
    const now = Date.now();
    let timestamps: number[] = [];
    try {
      const stored = localStorage.getItem(RATE_LIMIT_KEY);
      timestamps = stored ? JSON.parse(stored) : [];
    } catch { timestamps = []; }
    const recent = timestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
    if (recent.length >= RATE_LIMIT_MAX_REQUESTS) {
      const waitMs = RATE_LIMIT_WINDOW_MS - (now - Math.min(...recent));
      const waitMins = Math.ceil(waitMs / 60000);
      return `You've reached the reading limit. Please wait ${waitMins} minute${waitMins !== 1 ? "s" : ""} before your next reading.`;
    }
    return null;
  } catch {
    return null;
  }
}

export function checkClientRateLimit(): void {
  try {
    const bypassKey = process.env.REACT_APP_RATE_LIMIT_BYPASS_KEY;
    if (bypassKey) {
      const stored = localStorage.getItem("fatespin_rl_bypass");
      if (stored === bypassKey) return;
    }

    const now = Date.now();
    let timestamps: number[] = [];
    try {
      const stored = localStorage.getItem(RATE_LIMIT_KEY);
      timestamps = stored ? JSON.parse(stored) : [];
    } catch {
      timestamps = [];
    }
    const recent = timestamps.filter((t) => now - t < RATE_LIMIT_WINDOW_MS);
    if (recent.length >= RATE_LIMIT_MAX_REQUESTS) {
      const waitMs = RATE_LIMIT_WINDOW_MS - (now - Math.min(...recent));
      throw new RateLimitError(Math.ceil(waitMs / 60000));
    }
    recent.push(now);
    try { localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recent)); } catch { /* storage blocked */ }
  } catch (e) {
    if (e instanceof RateLimitError) throw e;
    // Any other localStorage error — don't block the reading
  }
}

interface CardRef {
  name: string;
  isReversed: boolean;
}

export async function logStaticReading(
  cardName: string,
  isReversed: boolean,
  question: string,
  originalCard?: CardRef,
): Promise<void> {
  const sessionId = getOrCreateSessionId();
  const token = await getAccessToken();
  try {
    await fetch("/api/log-reading", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ cardName, isReversed, question, sessionId, originalCard }),
    });
  } catch {
    // fire-and-forget — don't surface logging errors to the user
  }
}

export async function logSpreadReading(
  spreadType: SpreadType,
  spreadCards: Record<string, { name: string; isReversed: boolean }>,
  question: string,
  isAiReading: boolean,
): Promise<void> {
  const sessionId = getOrCreateSessionId();
  const token = await getAccessToken();
  try {
    await fetch("/api/log-reading", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ spreadType, spreadCards, question, sessionId, isAiReading }),
    });
  } catch {}
}

export async function logSpreadFocus(
  spreadType: SpreadType,
  focusCard: { name: string; isReversed: boolean },
  question: string,
): Promise<void> {
  const sessionId = getOrCreateSessionId();
  const token = await getAccessToken();
  try {
    await fetch("/api/log-reading", {
      method: "POST",
      headers: authHeaders(token),
      body: JSON.stringify({ spreadFocusCard: focusCard, spreadType, question, sessionId }),
    });
  } catch {}
}

export async function* streamTarotReading(
  cardName: string,
  isReversed: boolean,
  question: string,
  originalCard?: CardRef,
  signal?: AbortSignal,
  previousClarifyingCards?: CardRef[],
  language?: string,
  spreadType?: SpreadType,
  spreadPosition?: string,
  previousSpreadCards?: CardRef[],
  skipRateLimit?: boolean,
  isYesOrNo?: boolean,
): AsyncGenerator<string, void, unknown> {
  if (!skipRateLimit) checkClientRateLimit();

  const sessionId = getOrCreateSessionId();
  const token = await getAccessToken();

  const response = await fetch("/api/reading", {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({ cardName, isReversed, question, originalCard, sessionId, previousClarifyingCards, previousSpreadCards, language, spreadType, spreadPosition, isYesOrNo }),
    signal,
  });

  if (response.status === 429) {
    const body = await response.json().catch(() => null) as Record<string, unknown> | null;
    if (body?._debug) console.warn("[rate-limit-debug]", body._debug);
    throw new RateLimitError(60);
  }

  if (response.status === 422) {
    throw new Error("You've drawn the maximum of 5 clarifying cards for this reading.");
  }

  if (!response.ok) {
    throw new Error(`Reading service error ${response.status}`);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() ?? "";

    for (const line of lines) {
      if (!line.startsWith("data: ")) continue;
      const data = line.slice(6).trim();
      if (data === "[DONE]") return;
      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(data) as Record<string, unknown>;
      } catch {
        continue;
      }
      if (parsed.type === "error") {
        const msg = (parsed.error as Record<string, string> | undefined)?.message;
        throw new Error(msg ?? "The reading service is temporarily unavailable. Please try again shortly.");
      }
      if (parsed.type === "content_block_delta") {
        const delta = parsed.delta as Record<string, unknown> | undefined;
        if (delta?.type === "text_delta") {
          yield delta.text as string;
        }
      }
    }
  }
}
